/**
 * Convierte una `FormData` con archivos a un payload JSON serializable
 * apto para encolar en IndexedDB. Cada `File` entry se transforma a
 * `{ _file: base64, _name, _type, _size, _kind: 'inline-upload' }`.
 *
 * Límites:
 *   - Cada archivo ≤ 5 MB (configurable via `maxBytes`). Si excede,
 *     lanza `OfflineUploadTooLargeError`.
 *   - El JSON resultante hereda el límite de IndexedDB (~50% de la
 *     cuota del navegador) — para uploads de campo (foto de checklist,
 *     recibo) sobra; PDFs grandes generados por el servidor no van
 *     por este camino.
 *
 * El backend reconstruye el `Express.Multer.File` desde estos campos
 * via `AcceptInlineBase64Pipe` (ver `backend/src/common/idempotency/`).
 */

const DEFAULT_MAX_BYTES = 5 * 1024 * 1024;

export class OfflineUploadTooLargeError extends Error {
  readonly fileName: string;
  readonly size: number;
  readonly maxBytes: number;

  constructor(fileName: string, size: number, maxBytes: number) {
    super(
      `El archivo "${fileName}" pesa ${(size / 1024 / 1024).toFixed(1)} MB. El máximo offline son ${(maxBytes / 1024 / 1024).toFixed(0)} MB; conéctate para subirlo.`,
    );
    this.name = 'OfflineUploadTooLargeError';
    this.fileName = fileName;
    this.size = size;
    this.maxBytes = maxBytes;
  }
}

export class OfflineUploadEmptyError extends Error {
  readonly fileName: string;

  constructor(fileName: string) {
    super(`El archivo "${fileName}" está vacío (0 bytes). Selecciona uno con contenido.`);
    this.name = 'OfflineUploadEmptyError';
    this.fileName = fileName;
  }
}

export interface InlineUpload {
  _kind: 'inline-upload';
  _file: string; // base64 sin prefijo `data:...;base64,`
  _name: string;
  _type: string;
  _size: number;
}

export interface EncodeOptions {
  /// Bytes máximos por archivo. Default 5 MB.
  maxBytes?: number;
}

/**
 * Codifica `FormData` → `Record<string, unknown>` JSON-safe. Los
 * campos escalares se preservan; los `File` se transforman.
 *
 * Si una entry repetida (mismo name) tiene múltiples valores, los
 * agrupa en array. Igual que el comportamiento natural de FormData.
 */
export async function encodeFormDataInline(
  form: FormData,
  options: EncodeOptions = {},
): Promise<Record<string, unknown>> {
  const maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES;
  const out: Record<string, unknown> = {};

  for (const [key, value] of form.entries()) {
    const encoded = await encodeOne(value, maxBytes);
    if (out[key] === undefined) {
      out[key] = encoded;
    } else if (Array.isArray(out[key])) {
      (out[key] as unknown[]).push(encoded);
    } else {
      out[key] = [out[key], encoded];
    }
  }

  return out;
}

async function encodeOne(value: FormDataEntryValue, maxBytes: number): Promise<unknown> {
  if (typeof value === 'string') return value;
  // value es un File (Blob).
  const file = value as File;
  // Validación defensiva: el browser puede entregar Files con size 0
  // (drag&drop accidental, archivos corruptos) o sin .name (Blobs sin
  // metadata). Encodar base64 vacío rompe el reconstructor backend con
  // un buffer de 0 bytes — fallamos pronto con mensaje claro.
  if (file.size === 0) {
    throw new OfflineUploadEmptyError(file.name || 'archivo');
  }
  if (file.size > maxBytes) {
    throw new OfflineUploadTooLargeError(
      file.name || 'archivo',
      file.size,
      maxBytes,
    );
  }
  const base64 = await blobToBase64(file);
  // Si `file.name` es undefined o vacío, generamos uno determinístico
  // para que el backend tenga un nombre con extensión válida.
  const safeName = file.name && file.name.length > 0
    ? file.name
    : `upload-${Date.now()}.bin`;
  const upload: InlineUpload = {
    _kind: 'inline-upload',
    _file: base64,
    _name: safeName,
    _type: file.type || 'application/octet-stream',
    _size: file.size,
  };
  return upload;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('FileReader returned non-string'));
        return;
      }
      // Quita el prefijo `data:<mime>;base64,`.
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Helper para detectar si un payload encolado tiene uploads inline.
 * Útil para el sync engine si quisiéramos hacer logging diferenciado
 * (no necesario hoy).
 */
export function hasInlineUpload(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false;
  for (const v of Object.values(payload as Record<string, unknown>)) {
    if (
      v &&
      typeof v === 'object' &&
      '_kind' in v &&
      (v as InlineUpload)._kind === 'inline-upload'
    ) {
      return true;
    }
    if (Array.isArray(v)) {
      for (const item of v) {
        if (
          item &&
          typeof item === 'object' &&
          '_kind' in item &&
          (item as InlineUpload)._kind === 'inline-upload'
        ) {
          return true;
        }
      }
    }
  }
  return false;
}
